import { useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { ActorRefFrom } from 'xstate';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { localStorage } from '@change-corgi/core/storage';

import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from 'src/app/shared/api/petitionDraft';

import type { machine } from '../../orchestrationMachine';

type ActorRefType = ActorRefFrom<typeof machine>;

export function useSubscribeToMachine(actorRef: ActorRefType): void {
	const { errorReporter } = useUtilityContext();
	const queryClient = useQueryClient();
	const draftClient = useMemo(() => {
		return new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);
	}, [queryClient]);

	useEffect(() => {
		const subscription = actorRef.subscribe((currentSnapshot) => {
			const { draft: currentDraft } = currentSnapshot.context;
			if (!currentDraft || !currentDraft.id || !currentDraft.authorId) return;
			const { authorId, ...petitionDraft } = currentDraft;

			// Fire-and-forget: intentionally not awaiting the promise as updates should not block UI.
			void (async () => {
				try {
					await draftClient.updateDraft(authorId, petitionDraft);
				} catch (error) {
					void errorReporter.report(new Error('SAP: Could not update draft', { cause: error }));
				}
			})();
		});
		return () => subscription.unsubscribe();
	}, [actorRef, errorReporter]);
}
